import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";

import { yupResolver } from "@hookform/resolvers/yup";
import useGetPartner from "@hooks/admin/partners/useGetPartner";
import { InputLabel, MenuItem, Select } from "@material-ui/core";
import {
    Button, Card, Feedback, FormGroup, FormGroupMessage, Label, TextField, Title
} from "@runwayhealth/runway-components-react";
import { Partner, PartnerType } from "@types-runway/state";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  partnerType: yup.string(),
  spreadsheetId: yup
    .string()
    .test(
      "isValidSpreadsheetId",
      "A valid spreadsheet ID must be contain 44 characters",
      function (value) {
        if (!value) return true;

        if (value.length > 0 && value.length !== 44) {
          return false;
        }

        return true;
      }
    ),
  promocodes: yup.array<string[]>(),
});

type PartnerFormProps = {
  partnerId?: string | null;
  onSubmit: (partner: Partner) => void;
};

const PartnerForm: React.FC<PartnerFormProps> = ({ partnerId, onSubmit }) => {
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors },
    clearErrors,
  } = useForm<Partner>({
    resolver: yupResolver(schema),
  });

  const { getPartnerDetails } = useGetPartner();
  const [promocodeField, setPromocodeField] = useState("");
  const [promocodes, setPromocodes] = useState<string[]>([]);

  useMemo(() => {
    if (partnerId) {
      getPartnerDetails(partnerId).then((partner) => {
        if (partner) {
          const { name, spreadsheetId, type, promocodes } = partner;
          setValue("name", name);
          setValue("spreadsheetId", spreadsheetId);
          setValue("type", type);
          setPromocodes(promocodes);
        }
      });
    }
  }, [partnerId]);

  return (
    <section>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <Title>Partner Info</Title>
          <FormGroup>
            <Label htmlFor="name">Name*</Label>
            <TextField id="name" isInvalid={errors.name} {...register("name")} />
            <FormGroupMessage>
              {errors.name && (
                <Feedback size="sm" isInvalid>
                  {errors.name?.message}
                </Feedback>
              )}
            </FormGroupMessage>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="spreadsheetId">Spreadsheet ID</Label>
            <TextField
              id="spreadsheetId"
              isInvalid={errors.spreadsheetId}
              {...register("spreadsheetId")}
            />
            <FormGroupMessage>
              {errors.spreadsheetId && (
                <Feedback size="sm" isInvalid>
                  {errors.spreadsheetId?.message}
                </Feedback>
              )}
            </FormGroupMessage>
          </FormGroup>
          <FormGroup>
            <InputLabel id="partner-type-selector-label">Select a partner type</InputLabel>
            <Controller
              control={control}
              name="type"
              render={({ field: { name, onBlur } }) => (
                <Select
                  name={name}
                  labelId="partner-type-selector-label"
                  id="partner-type-selector"
                  label="Select a partner type"
                  value={getValues("type") ?? "Other"}
                  onBlur={onBlur}
                  onChange={(e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
                    setValue("type", e.target.value as PartnerType);
                    clearErrors("type");
                  }}
                >
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              )}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="promocodes">Promocodes</Label>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <TextField
                value={promocodeField}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  setPromocodeField(e.currentTarget.value)
                }
              />
              <Button
                isIconOnly
                iconName="ListPlus"
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  // Prevent code adding from executing submit.
                  e.preventDefault();
                  if (Boolean(promocodeField)) {
                    const currentCodes = Boolean(getValues("promocodes"))
                      ? getValues("promocodes")
                      : [];
                    currentCodes.push(promocodeField);
                    setValue("promocodes", currentCodes);
                    setPromocodes(currentCodes);
                  }
                }}
              />
            </div>
            <table>
              <tbody>
                {promocodes.map((promocode: string) => (
                  <tr>
                    <td>{promocode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FormGroup>
          <Button onClick={() => (partnerId = null)}>Reset</Button>
          <Button type="submit">Save</Button>
        </Card>
      </form>
    </section>
  );
};

export default PartnerForm;
